import View                      from '@widesk-ui/components/View';
import Popover                   from '@widesk-ui/components/Popover';
import { observer }              from 'mobx-react-lite';
import React                     from 'react';
import { BorderOutlined }        from '@ant-design/icons';
import { LineOutlined }          from '@ant-design/icons';
import { GatewayOutlined }       from '@ant-design/icons';
import { PictureOutlined }       from '@ant-design/icons';
import { DownOutlined }          from '@ant-design/icons';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import openImportImageModal      from '@/TokamapEditor/helpers/openImportImageModal';
import CircleOutlined            from '@/TokamapEditor/ux/icons/CircleOutlined';
import ShapesIcon                from '@/TokamapEditor/ux/icons/ShapesIcon';
import Kbd                       from '@/TokamapEditor/ux/Kbd';
import useDesignTokens           from '@/hooks/useDesignTokens';
import { FunctionalityLockMark } from '@/components/Functionality';

type ShapeKind = 'rectangle' | 'polygon' | 'circle' | 'line' | 'image';

// `image` est bien un outil de tracé comme les autres, malgré son étape de modale : `FileForm` ne
// sert qu'à choisir le fichier, puis `startDrawingImage` arme un geste à 2 clics coin à coin (voir
// `adapters/types.ts`). `drawing:started` la renvoie donc au même titre que les autres, et son
// entrée s'affiche bien comme sélectionnée pendant le tracé.
const tools: { key: ShapeKind; label: string; shortCut: string; icon: React.ReactNode; lockMark?: React.ReactNode; onClick: () => void; }[] = [
	{
		key: 'rectangle',
		label: 'Rectangle',
		shortCut: 'R',
		icon: <BorderOutlined style={{ fontSize: 16 }} />,
		onClick: () => currentEditorStore.get.startDrawingRectangle(),
	},
	{
		key: 'polygon',
		label: 'Polygone',
		shortCut: 'P',
		icon: <GatewayOutlined style={{ fontSize: 16 }} />,
		onClick: () => currentEditorStore.get.startDrawingPolygon(),
	},
	{
		key: 'circle',
		label: 'Cercle',
		shortCut: 'C',
		icon: <CircleOutlined style={{ fontSize: 16 }} />,
		onClick: () => currentEditorStore.get.startDrawingCircle(),
	},
	{
		key: 'line',
		label: 'Ligne',
		shortCut: 'L',
		icon: <LineOutlined style={{ fontSize: 16 }} />,
		onClick: () => currentEditorStore.get.startDrawingLine(),
	},
	{
		key: 'image',
		label: 'Image',
		shortCut: 'I',
		icon: <PictureOutlined style={{ fontSize: 16 }} />,
		lockMark: <FunctionalityLockMark reference="nb_images" />,
		onClick: () => openImportImageModal(),
	},
];

/**
 * Segment "Dessiner" de la barre flottante (voir `MapFloatingControls`) : les outils de dessin
 * appartiennent à la carte, plus à une barre latérale d'application. Même structure que `MapButton`
 * (icône + libellé + chevron, relief porté par la barre) pour que les deux segments se lisent comme
 * un même objet.
 *
 * Un verbe et non "Formes" : aucune de ces entrées ne crée quoi que ce soit au clic, elles arment un
 * mode et attendent un geste sur la carte — y compris "Image", dont la modale ne fait que choisir le
 * fichier avant `startDrawingImage`. Un nom au pluriel se lisait de surcroît comme un inventaire des
 * formes de la carte, à l'image du menu "Vos cartes" juste à côté dans la même barre.
 *
 * "Dessiner" et non "Tracer" : plus courant, donc reconnu plus vite. "Tracer" est plus juste sur le
 * plan technique, mais l'écart de clarté entre les deux est nul et le premier se lit sans effort.
 */
const ShapesButton = observer(() => {
	const tokens = useDesignTokens();
	const editor = currentEditorStore.get;

	const [opened, setOpened] = React.useState(false);
	const [drawingKind, setDrawingKind] = React.useState<ShapeKind>();

	React.useEffect(() => {
		if (!editor) return;

		return editor.mapAdapter.on(event => {
			if (event.type === 'drawing:started') return setDrawingKind(event.shapeKind);
			if (event.type === 'drawing:ended') return setDrawingKind(undefined);
		});
	}, [editor]);

	// Un outil en cours prime sur l'ouverture du menu : le segment reste teinté tant qu'on dessine,
	// ce qui rappelle que le prochain clic sur la carte trace une forme.
	const active = !!drawingKind || opened;

	return (
		<Popover
			trigger="click"
			placement="bottomRight"
			open={opened}
			onOpenChange={setOpened}
			styles={{ body: { padding: 0 } }}
			content={(
				<View p="xs" gap={2} style={{ width: 240 }}>
					{tools.map(tool => (
						<View
							key={tool.key}
							row
							centerV
							gap="sm"
							px="sm"
							py="xs"
							br={8}
							pointer
							color={tokens.text.default}
							bg={drawingKind === tool.key ? tokens.fill.subtle : undefined}
							hover={{ bg: tokens.surface.subtle }}
							onClick={() => {
								setOpened(false);
								tool.onClick();
							}}
						>
							{tool.icon}

							<View flex>{tool.label}</View>

							{tool.lockMark}

							<Kbd>{tool.shortCut}</Kbd>
						</View>
					))}
				</View>
			)}
		>
			<View
				row
				centerV
				gap="xs"
				px="sm"
				h="100%"
				pointer
				color={tokens.text.default}
				bg={active ? tokens.surface.subtle : undefined}
				hover={{ bg: tokens.surface.subtle }}
			>
				<ShapesIcon size={17} />

				<View bold>Tracer</View>

				<DownOutlined
					style={{ fontSize: 10, opacity: 0.55, transition: 'transform .2s ease' }}
					rotate={opened ? 180 : 0}
				/>
			</View>
		</Popover>
	);
});

export default ShapesButton;
